import { Button, Col, LoaderOverlay, Row, SwitchNav, Form } from "@emanprague/ppaskit";
import iconDownloadBlue from "@emanprague/ppaskit/dist/assets/icons/Download Blue.svg";
import { Check, CollectionCheck, Input } from "@frui.ts/bootstrap";
import { ResponsiveTable } from "@frui.ts/dataviews";
import type { ViewComponent } from "@frui.ts/views";
import { preventDefault, registerView, View } from "@frui.ts/views";
import EmptyDataMessage from "components/EmptyDataMessage";
import { getColoredLabelFinance } from "helpers/labelHelper";
import FilterButton from "controls/filterButton";
import SupplyPointInline from "controls/supplyPointInline";
import type { DataColumn } from "controls/tableHelper";
import { createColumnsContext } from "controls/tableHelper";
import DatePicker from "controls/twoway/DatePicker";
import RangeInput from "controls/twoway/RangeInput";
import SupplyPointSelect from "controls/twoway/SupplyPointSelect";
import type PaymentListPaymentsItem from "entities/paymentListPaymentsItem";
import { getSupplyPointTypeIcon } from "helpers/supplyPointHelper";
import { observer } from "mobx-react-lite";
import PaymentState from "models/paymentState";
import React from "react";
import PaymentsPageViewModel, { defaultAmountRange } from "viewModels/finance/paymentsPageViewModel";
import "./style.scss";
import PaymentItemsTooltip from "components/PaymentItemsTooltip";
import ExportButtons from "./exportButtons";

const columns: DataColumn<PaymentListPaymentsItem, PaymentsPageViewModel>[] = [
  {
    titleFactory: ({ vm }) => (
      <Check
        id="paymentCheckAll"
        custom
        target={vm}
        property="isAllSelected"
        threeState
        label={<div className="sr-only">{vm.translate("check_all_payment_context")}</div>}
      />
    ),
    valueFormatter: ({ item, context }) => (
      <CollectionCheck
        id={`paymentCheck-${item.id}`}
        custom
        label={
          <div className="sr-only">
            {context.vm.translate("check_payment_context", {
              vs: item.vs,
              date: context.vm.localization.formatDate(item.date),
            })}
          </div>
        }
        target={context.vm}
        property="selectedItems"
        value={item}
      />
    ),
    responsiveVisible: false,
  },
  {
    property: "supplyPoints",
    titleFactory: ({ vm, tg }) => (
      <>
        <span id="supplyPointIdsFilterTitle">{tg("general.filter_supply_point")}</span>
        <FilterButton
          id="supplyPointIdsFilter"
          isActive={!!vm.filter.supplyPointIds?.length}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("supplyPointIds")}
          onConfirm={vm.applyFilterAndLoad}>
          <SupplyPointSelect
            target={vm.filter}
            property="supplyPointIds"
            supplyPoints={vm.supplyPoints}
            multiselect
            translateFunc={vm.translateGeneral}
          />
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ vm, tg }) => tg("general.filter_supply_point"),
    valueFormatter: valueProps => (
      <>
        {valueProps.item.supplyPoints && (
          <>
            {valueProps.item.supplyPoints?.length === 1 &&
              valueProps.item.supplyPoints.map(x => (
                <SupplyPointInline
                  key={x.id}
                  supplyPoint={x}
                  commodityType={valueProps.context.vm.enumsService.getCommodityType(x.commodityId)}
                  commodityAlt={valueProps.context.vm.getCommodityIconAlt(x.commodityId)}
                />
              ))}

            {valueProps.item.supplyPoints?.length > 1 && (
              <>
                <img
                  className="mr-2"
                  width="22"
                  height="21"
                  draggable={false}
                  src={getSupplyPointTypeIcon(
                    valueProps.context.vm.enumsService.getCommodityType(valueProps.item.supplyPoints[0].commodityId),
                    "yellowAndLightBlue"
                  )}
                  role="presentation"
                />
                {getColoredLabelFinance(
                  valueProps,
                  valueProps.item.supplyPoints.length.toString() +
                    " " +
                    valueProps.context.tm("supply_point", valueProps.item.supplyPoints.length).toLowerCase()
                )}
              </>
            )}
          </>
        )}
      </>
    ),
  },
  {
    property: "type",
    titleFactory: ({ vm, ta }) => (
      <>
        <Form.Label id="typeFilterTitle" htmlFor="type-search-field">
          {ta("payment", "type")}
        </Form.Label>
        <FilterButton
          id="typeFilter"
          isActive={!!vm.filter.type}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("type")}
          onConfirm={vm.applyFilterAndLoad}>
          <Input id="type-search-field" target={vm.filter} property="type" placeholder={vm.translateGeneral("search")} />
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ vm, ta }) => ta("payment", "type"),
  },
  {
    property: "vs",
    titleFactory: ({ vm, ta }) => (
      <>
        <Form.Label id="vsFilterTitle" htmlFor="vs-search-field">
          {ta("payment", "vs")}
        </Form.Label>
        <FilterButton
          id="vsFilter"
          isActive={!!vm.filter.vs}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("vs")}
          onConfirm={vm.applyFilterAndLoad}>
          <Input id="vs-search-field" target={vm.filter} property="vs" placeholder={vm.translateGeneral("search")} />
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ vm, ta }) => ta("payment", "vs"),
  },
  {
    property: "date",
    titleFactory: ({ vm, ta }) => (
      <>
        <span id="dateFilterTitle">{ta("payment", "paid_date")}</span>
        <FilterButton
          id="dateFilter"
          isActive={!!vm.filter.dateFrom || !!vm.filter.dateTo}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("dateFrom", "dateTo")}
          onConfirm={vm.applyFilterAndLoad}>
          <Row className="align-items-center">
            <Col className="mr-n3">
              <Form.Label htmlFor="date-filter-from" className="date-filter" aria-describedby="dateFilterTitle">
                {vm.translateGeneral("filter_date_from")}
              </Form.Label>
              <DatePicker
                id="date-filter-from"
                target={vm.filter}
                property="dateFrom"
                selectsStart
                startDate={vm.filter.dateFrom}
                endDate={vm.filter.dateTo}
                maxDate={vm.filter.dateTo}
              />
            </Col>
            <Col>
              <Form.Label htmlFor="date-filter-to" className="date-filter to" aria-describedby="dateFilterTitle">
                {vm.translateGeneral("filter_date_to")}
              </Form.Label>
              <DatePicker
                id="date-filter-to"
                target={vm.filter}
                property="dateTo"
                selectsEnd
                startDate={vm.filter.dateFrom}
                endDate={vm.filter.dateTo}
                minDate={vm.filter.dateFrom}
              />
            </Col>
          </Row>
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ vm, ta }) => ta("payment", "paid_date"),
    valueFormatter: ({ value, context }) => context.vm.localization.formatDate(value),
  },
  {
    property: "amount",
    titleFactory: ({ vm, ta }) => (
      <>
        <span id="amountFilterTitle">{ta("payment", "amount")}</span>
        <FilterButton
          id="amountFilter"
          isActive={!!vm.filter.minAmount || !!vm.filter.maxAmount}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("minAmount", "maxAmount")}
          onConfirm={vm.applyFilterAndLoad}>
          <RangeInput
            target={vm}
            property="amountRange"
            allowUserOverride={true}
            min={defaultAmountRange[0]}
            max={defaultAmountRange[1]}
            step={100}
            disabled={false}
            autofocus
            suffix={" " + vm.translateGeneral("currency")}
            name="rangeslider"
            form="rangeform"
            controlAriaDescribedBy="amountFilterTitle"
          />
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ vm, ta }) => ta("payment", "amount"),
    valueFormatter: ({ item, value, context }) => (
      <span className="text-nowrap">
        {context.vm.localization.formatNumber(value)} {context.vm.translateGeneral("currency")}
        &nbsp;
        <PaymentItemsTooltip
          total_amount={item.amount}
          formatNumber={context.vm.localization.formatNumber}
          translateGeneral={context.vm.translateGeneral}
          items={item.paymentItems}
        />
      </span>
    ),
    cellClassName: "cell-right",
    headerClassName: "cell-right",
  },
  {
    titleFactory: ({ vm }) => vm.translate("invoice"),
    valueFormatter: ({ item, context }) => {
      if (item.documentId || item.file) {
        return (
          <>
            {item.documentId}
            &nbsp; &nbsp;
            {item.file && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => context.vm.downloadDocument(item)}
                aria-label={context.vm.getActionButtonAriaContext(item)}>
                <img src={iconDownloadBlue} width={16} height={16} role="presentation" />{" "}
                {context.vm.localization.translateGeneral("general.download_button")}
              </Button>
            )}
          </>
        );
      }
    },
    cellClassName: "cell-right",
  },
];

const paymentsPageListView: ViewComponent<PaymentsPageViewModel> = observer(({ vm }) => {
  const { translateGeneral: tg } = vm.localization;

  const navLinks = [
    {
      id: "all",
      title: `${tg("general.filter_all")}`,
      href: "#",
      onClick: preventDefault(() => vm.applyPaymentStateFilter()),
    },
    {
      id: PaymentState.Paid,
      title: `${vm.translate("filter_paid")}`,
      href: "#",
      onClick: preventDefault(() => vm.applyPaymentStateFilter(PaymentState.Paid)),
    },
    {
      id: PaymentState.Returned,
      title: `${vm.translate("filter_returned")}`,
      href: "#",
      onClick: preventDefault(() => vm.applyPaymentStateFilter(PaymentState.Returned)),
    },
  ];

  return (
    <LoaderOverlay loading={vm.busyWatcher.isBusy}>
      <Row className="mb-3 justify-content-between align-items-center">
        <Col xs={12} md="auto" className="py-2">
          <SwitchNav
            links={navLinks}
            size="sm"
            activeId={vm.activePaymentState}
            ariaDescribedBy={vm.navigationName}
            legend={vm.name}
          />
        </Col>
        <Col xs={12} md="auto" className="py-2" style={{ display: "flex", gap: "0.5rem" }}>
          {vm.canDownload && <ExportButtons vm={vm} />}
        </Col>
        <Col xs={12} md={4} className="py-2">
          <Form.Label srOnly htmlFor="search-field">
            {vm.translate("search_placeholder")}
          </Form.Label>
          <Input
            id="search-field"
            size="sm"
            target={vm.filter}
            property="search"
            placeholder={vm.translate("search_placeholder")}
            onValueChanged={vm.applyFilterAndLoadDebounced}
          />
        </Col>
      </Row>
      <div className="responsive-table-wrapper">
        <ResponsiveTable
          className="table no-wrap"
          items={vm.items}
          itemKey="id"
          columns={columns}
          context={createColumnsContext(vm)}
        />
      </div>
      {!vm.items?.length && <EmptyDataMessage translateGeneral={tg} />}

      {vm.canLoadData && (
        <div className="load-more-button">
          <Button onClick={vm.loadData} variant="secondary">
            {tg("general.button_load_more")}
          </Button>
        </div>
      )}

      <View vm={vm.activeChild} />
    </LoaderOverlay>
  );
});

registerView(paymentsPageListView, PaymentsPageViewModel);
